import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { switchTemporality } from 'src/app/shared/utils/switch-temporality';
import { EnumProfitsOptions } from 'src/app/shared/enums/profits-options.enum';
import { WebsocketPseService } from 'src/app/services/websocket-pse.service';
import { IWebSocketParams } from 'src/app/shared/interfaces/web-socket-params.interface';
import { environment } from 'src/environments/environment';
import { IWebSocketPseSendMessage } from 'src/app/shared/interfaces/web-socket-pse-send-message.interface';
import { routesData } from 'src/app/shared/resources/routes.constant';
import { EnumStepFunctions } from 'src/app/shared/enums/step-functions.enum';
import { Subscription } from 'rxjs';
import { EnumTemporality } from 'src/app/shared/enums/temporality.enum';
import { IProductSummary } from 'src/app/shared/interfaces/product-summary.interface';
import { IDataVoucher } from 'src/app/shared/interfaces/data-voucher.interface';
import { VoucherComponent } from 'src/app/shared/components/voucher/voucher.component';
import { MoreInfoService } from 'src/app/shared/services/more-info.service';
import { EnumPseStatusTransaction } from 'src/app/shared/enums/pse-status-transaction.enum';
import { AnalyticService } from 'src/app/shared/services/analytic.service';
import { CdtCalculatorService } from 'src/app/shared/services/cdt-calculator.service';
import { DialogOptionsService } from 'src/app/shared/services/dialog-options.service';
import { BreakpointObserver } from '@angular/cdk/layout';

@Component({
  selector: 'app-product-summary-pse',
  templateUrl: './product-summary-pse.component.html',
  styleUrls: ['./product-summary-pse.component.scss']
})
export class ProductSummaryPseComponent implements OnInit, OnDestroy {
  name: string = '';
  cdtAccountNumber: string = '';
  cdtAmount: number = 0;
  cdtTimeTerm: number = 0;
  interestRate: string = '';
  profitsAccount: string = '';
  renovationCDT: boolean = false;
  termination: EnumTemporality = {} as EnumTemporality;
  officeProfits: boolean = false;
  digitalProfits: boolean = false;
  accountNumber: string = '';
  subscriptions = new Subscription();
  showSummary = false;
  pendingPSE = false;
  dataVoucher: IDataVoucher = {} as IDataVoucher;
  assetsUrl = environment.assetsUrl;
  showOfficeCardAlert: boolean = false;
  simulationData = {
    rate: 0,
    totalProfit: '',
    profit: '',
    retention: '',
    earnings: ''
  };
  tagAval: string = '';

  @ViewChild(VoucherComponent)
  voucher: VoucherComponent = {} as VoucherComponent;

  get terminationSwitch(): string {
    return switchTemporality(this.termination);
  }

  constructor(
    public websocketPse: WebsocketPseService,
    public router: Router,
    public moreInfoService: MoreInfoService,
    public analytic: AnalyticService,
    private cdtCalculatorService: CdtCalculatorService,
    private dialogOptionsService: DialogOptionsService,
    public breakpointObserver: BreakpointObserver
  ) {}

  ngOnInit(): void {
    if (typeof Storage !== 'undefined') {
      const pickUpInOffice = sessionStorage.getItem('pickupInOffice');
      if (pickUpInOffice === 'true') {
        this.showOfficeCardAlert = true;
      }
    }
    this.callWebsocketPse();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  processSummaryData(bodyRST: IProductSummary): void {
    this.calculateEarnings(bodyRST);
    this.showSummary = true;
    this.name = bodyRST.firstName;

    this.officeProfits = bodyRST?.profitsOption === EnumProfitsOptions.Office;

    this.digitalProfits = bodyRST?.profitsOption === EnumProfitsOptions.Digital;

    this.accountNumber = bodyRST?.accountNumber;
    this.cdtAccountNumber = bodyRST.cdtAccountNumber;
    this.cdtAmount = +bodyRST.cdtAmount;
    this.cdtTimeTerm = bodyRST.cdtTerm;
    this.interestRate = bodyRST.interestRate.toString();
    this.profitsAccount = bodyRST?.cdtProfitsAcct;
    this.renovationCDT = bodyRST.cdtRenovation;
    this.termination = +bodyRST.cdtTermination;

    this.dataVoucher = {
      foundWithPse: true,
      firstName: this.name,
      accountNumber: this.accountNumber,
      cdtAccountNumber: this.cdtAccountNumber,
      cdtAmount: this.cdtAmount,
      cdtProfitsAcct: this.profitsAccount,
      cdtRenovation: this.renovationCDT,
      cdtTerm: this.cdtTimeTerm,
      cdtTermination: this.termination,
      digitalProfits: this.digitalProfits,
      interestRate: this.interestRate,
      officeProfits: this.officeProfits,
      isAssistedSaleWithPse: false,
      cardSelected: this.moreInfoService.getMoreInfo().cardSelected,
      simulationData: this.simulationData
    };
  }

  calculateEarnings(bodyRST: IProductSummary) {
    const simulationData =
      this.cdtCalculatorService.calculateFinantialReturnsFromReadSummaryTransaction(
        {
          days: bodyRST.cdtTerm,
          amount: bodyRST.cdtAmount,
          amountFormat: this.formatCurrency(bodyRST.cdtAmount),
          earningsPeriod: +bodyRST.cdtTermination
        },
        bodyRST.interestRate
      );
    this.simulationData.profit = `+ ${this.formatCurrency(
      simulationData.profit + ''
    )}`;
    this.simulationData.retention = `- ${this.formatCurrency(
      simulationData.retention + ''
    )}`;
    this.simulationData.totalProfit = `${this.formatCurrency(
      +simulationData.amount + +simulationData.earnings + ''
    )}`;
  }

  callWebsocketPse(): void {
    try {
      const documentQueryParam =
        this.router.routerState.snapshot.root.queryParams['documentNumber'];
      let documentNumber = documentQueryParam;

      if (documentQueryParam.indexOf('?token') !== -1) {
        const indexNewQueryParam = documentQueryParam.indexOf('?token');
        documentNumber = documentQueryParam.substr(0, indexNewQueryParam);
      }

      const paramsWS: IWebSocketParams = {
        fromObject: {
          authorization:
            this.router.routerState.snapshot.root.queryParams['tokenws'],
          transactionId:
            this.router.routerState.snapshot.root.queryParams['transactionId'],
          documentNumber
        },
        otherBaseUrlWs: environment.baseUrlWsPse
      };

      const bodyMessage: IWebSocketPseSendMessage = {
        action: 'pse-callback',
        contentType: 'application/json',
        documentType: 'CC',
        xAdlChannel: 'bavv-pasivo-cdt-masivo'
      };

      this.websocketPse.connectWebSocket(paramsWS);
      this.websocketPse.sendMessage(bodyMessage);
      this.waitWebSocket();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      let errorMessage = '';
      if (error?.message !== undefined) {
        errorMessage = error.message;
      }
      this.analytic.uTagLinkError(
        'Error catch callWebsocketPse ' + errorMessage,
        'error_pse_page'
      );
      this.router.navigate([routesData.ERRORS.PSE_ERROR.redirectPath]);
    }
  }

  waitWebSocket(): void {
    this.subscriptions.add(
      this.websocketPse.waitStepEndProcess().subscribe({
        next: (response) => {
          this.processResponse(response);
        },
        error: (err) => {
          // eslint-disable-next-line no-console
          console.error(err);
          const errorMessage = err.message === undefined ? '' : err.message;
          const event =
            errorMessage === 'Unauthorized'
              ? 'error_user_page'
              : 'error_tech_page';
          this.analytic.uTagLinkError(
            'Error WS product summary pse ' + errorMessage,
            event
          );
          if (errorMessage !== 'Unauthorized') {
            this.router.navigate([
              routesData.ERRORS.GENERAL_ERROR.redirectPath
            ]);
          }
        }
      })
    );
  }

  processResponse(response: any): void {
    const stepOk = response[EnumStepFunctions.StepEndProcess];
    const stepTS = response[EnumStepFunctions.StepTransactionStatus];
    const stepRST = response[EnumStepFunctions.StepReadSummaryTransaction];
    const stepFT = response[EnumStepFunctions.StepFinishTransaction];

    if (stepOk) {
      if (
        [200, 202].includes(stepTS?.statusCode) &&
        stepFT?.statusCode === 200
      ) {
        this.pendingPSE = stepTS.statusCode === 202;

        if (stepRST && stepRST.statusCode === 200) {
          this.processSummaryData(JSON.parse(stepRST.body));
        } else {
          this.analytic.uTagLinkError(
            'Error step RST statusCode: ' + stepRST.statusCode,
            'error_pse_page'
          );
          this.router.navigate([
            routesData.ERRORS.SUMMARY_PSE_ERROR.redirectPath
          ]);
        }
      } else if (stepTS && stepTS.statusCode === 206) {
        this.firstTimeFundingDecision(EnumPseStatusTransaction.ERROR, stepTS);
      } else if (stepTS && stepTS.statusCode === 202) {
        this.firstTimeFundingDecision(EnumPseStatusTransaction.PENDING);
      } else {
        this.analytic.uTagLinkError(
          'Error step TS statusCode: ' + stepTS.statusCode,
          'error_pse_page'
        );
        this.router.navigate([
          routesData.ERRORS.PSE_PENDING_ERROR.redirectPath
        ]);
      }
    } else {
      this.analytic.uTagLinkError('Error step endProcessOK', 'error_pse_page');
      this.router.navigate([routesData.ERRORS.PSE_PENDING_ERROR.redirectPath]);
    }
  }

  firstTimeFundingDecision(statusType: string, stepTS?: any) {
    if (!this.moreInfoService.moreInfoData.retakeCustomerDecision) {
      this.analytic.uTagLinkError('Error step TS no retoma', 'error_pse_page');
      statusType === EnumPseStatusTransaction.ERROR
        ? this.router.navigate([routesData.ERRORS.PSE_ERROR.redirectPath])
        : this.router.navigate([
            routesData.ERRORS.PSE_PENDING_ERROR.redirectPath
          ]);
    } else {
      this.retakeHasNotTaken(statusType, stepTS);
    }
  }

  openTagDialog() {
    this.dialogOptionsService.openDialogWithCustomOptions(
      'tagAval',
      'dialog-overlay-tagaval',
      {
        width: this.breakpointObserver.isMatched('(max-width: 420px)')
          ? '460px'
          : '436px',
        maxWidth: this.breakpointObserver.isMatched('(max-width: 420px)')
          ? '420px'
          : '440px'
      }
    );
  }

  downloadVoucher(): void {
    this.voucher.download();
  }

  retakeHasNotTaken(statusType: string, stepTS?: any) {
    if (statusType === EnumPseStatusTransaction.ERROR) {
      this.moreInfoService.setMoreInfo({
        ...this.moreInfoService.moreInfoData,
        cdtNumber: JSON.parse(stepTS.body).productNumber
      });
      this.moreInfoService.setMoreInfo({
        ...this.moreInfoService.moreInfoData,
        retakeReturnedByPse: true
      });
      this.router.navigate([routesData.PSE.redirectPath]);
    } else {
      this.pendingPSE = true;
      if (stepTS) {
        this.processResponse(stepTS.body);
      } else {
        this.analytic.uTagLinkError(
          'Error step TS retoma pendiente',
          'error_pse_page'
        );
        this.router.navigate([
          routesData.ERRORS.PSE_PENDING_ERROR.redirectPath
        ]);
      }
    }
  }

  formatCurrency(value: string): string {
    const numberValue = parseFloat(value);
    if (isNaN(numberValue)) {
      return value;
    }
    return `$ ${numberValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
  }
}
